<template>
  <div class="the-hub container-fluid main-content">
    <div class="row">
      <TheHubSidebar :active-slug="slug" />

      <section v-if="category" class="col-12 col-md-9 the-hub-category">
        <h1 class="page-title">{{ category.name }}</h1>

        <p v-if="parent" class="the-hub-category__parent">
          <a :href="categoryUrl(parent)" class="the-hub-category__parent-link">
            <span aria-hidden="true">&lsaquo;</span>
            <font-awesome-icon :icon="['fas', 'folder']" />
            <span
              >Part of <strong>{{ parent.name }}</strong></span
            >
          </a>
        </p>

        <nav v-if="children.length" class="the-hub-category__children" aria-labelledby="the-hub-subcategories-title">
          <h2 id="the-hub-subcategories-title" class="the-hub-category__children-title">Subcategories</h2>
          <ul>
            <li v-for="child in children" :key="child.id">
              <a :href="categoryUrl(child)">
                <font-awesome-icon :icon="['fas', 'folder']" />
                <span>{{ child.name }}</span>
                <span aria-hidden="true">&rsaquo;</span>
              </a>
            </li>
          </ul>
        </nav>

        <TheHubArticleList
          :articles="articles"
          :category-slug="slug"
          :loading="$fetchState.pending"
          :error="error"
          :total-pages="totalPages"
          :page="page"
          @change-page="onChangePage"
          @retry="$fetch"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { getCategoryBySlug, getParentCategory, getChildCategories, categoryPath } from '~/lib/data/the-hub-categories'
import { fetchArticles, parsePagination } from '~/lib/util/articles'

export default {
  name: 'TheHubCategory',
  layout: 'TheHub',
  components: {
    TheHubSidebar: () => import('~/components/TheHub/Sidebar.vue'),
    TheHubArticleList: () => import('~/components/TheHub/ArticleList.vue')
  },

  async fetch() {
    if (!this.category) return
    const { page, limit } = parsePagination({ ...this.$route.params, ...this.$route.query })
    const result = await fetchArticles({ context: this, page, limit, categorySlug: this.category.slug })
    this.error = result.statusCode !== 200
    this.articles = result.items
    this.total = result.total
    this.page = page
    this.limit = limit
  },

  // Category and paginated category routes are static; derive the category slug from the path.
  asyncData({ route, res, store }) {
    const segments = route.path.split('/').filter(Boolean)
    const pageIndex = segments.indexOf('page')
    const slug = segments[pageIndex > 0 ? pageIndex - 1 : segments.length - 1]
    const category = getCategoryBySlug(slug)
    if (!category) {
      if (res) res.statusCode = 404
      store.commit('UI/statusCode', 404, { root: true })
    } else {
      store.commit('UI/statusCode', 200, { root: true })
    }
    return { slug }
  },

  data() {
    return {
      articles: [],
      total: 0,
      page: 1,
      limit: parsePagination({ ...this.$route.params, ...this.$route.query }).limit,
      error: false
    }
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit)
    },
    category() {
      return getCategoryBySlug(this.slug)
    },
    parent() {
      return getParentCategory(this.category)
    },
    children() {
      return this.category ? getChildCategories(this.category) : []
    }
  },
  // Not watchQuery: in Nuxt 2 it resets data() but does not re-run the fetch() hook, leaving an empty list.
  watch: {
    '$route.fullPath': '$fetch'
  },

  methods: {
    onChangePage(page) {
      const query = {}
      if (page > 1) query.page = page
      if (this.$route.query.limit) query.limit = this.limit
      if (this.$route.query.useCache === 'false') query.useCache = 'false'
      this.$router.push({ path: categoryPath(this.category), query })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    categoryUrl(category) {
      return `${this.$config.baseURL}${categoryPath(category)}`
    }
  },

  head() {
    const robots = { hid: 'robots', name: 'robots', content: this.$config.robots || 'INDEX,FOLLOW' }
    if (!this.category) return { title: 'The Hub | magazine.co.uk', meta: [robots] }

    const baseUrl = this.categoryUrl(this.category)
    const pageUrl = page => (page > 1 ? `${baseUrl}?page=${page}` : baseUrl)
    const url = pageUrl(this.page)
    const description = `${this.category.name} articles on The Hub, the magazine.co.uk blog: news, features, reviews, gift guides and offers for magazine lovers.`
    const link = [{ hid: 'canonical', rel: 'canonical', href: url }]
    if (this.page > 1) link.push({ hid: 'prev', rel: 'prev', href: pageUrl(this.page - 1) })
    if (this.page < this.totalPages) link.push({ hid: 'next', rel: 'next', href: pageUrl(this.page + 1) })

    return {
      title: `${this.category.name}${this.page > 1 ? ` - Page ${this.page}` : ''} | The Hub | magazine.co.uk`,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'keywords', name: 'keywords', content: 'Magazine.co.uk' },
        robots,
        { hid: 'og:locale', property: 'og:locale', content: 'en_GB' },
        { hid: 'og:type', property: 'og:type', content: 'article' },
        { hid: 'og:title', property: 'og:title', content: `${this.category.name} Archives - The Hub` },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:url', property: 'og:url', content: url },
        { hid: 'og:site_name', property: 'og:site_name', content: 'magazine.co.uk' }
      ],
      link
    }
  }
}
</script>

<style lang="scss" scoped>
.the-hub {
  max-width: 1200px;
  margin: 0 auto;
}
.the-hub-category {
  .page-title {
    color: #e1000b;
    font-size: 40px;
    font-weight: 400;
    margin: 10px 0 10px;
  }
  &__parent,
  &__soon {
    margin: 15px 0;
  }
  &__parent-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: #f2f2f2;
    border: 1px solid #e9e9e9;
    border-radius: 20px;
    color: #363636;
    font-size: 14px;
    text-decoration: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    &:hover,
    &:focus {
      color: #363636;
      text-decoration: none;
      border-color: #b5b5b5;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }
    svg {
      color: #636467;
    }
  }
  &__children {
    margin: 15px 0;
    padding: 15px 18px;
    background: #f2f2f2;
    border: 1px solid #e9e9e9;
    border-radius: 4px;
  }
  &__children-title {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #636467;
  }
  &__children ul {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
    a {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      background: #fff;
      border: 1px solid #dadada;
      border-radius: 20px;
      color: #363636;
      font-size: 14px;
      text-decoration: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      &:hover,
      &:focus {
        color: #363636;
        text-decoration: none;
        border-color: #b5b5b5;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      }
      svg {
        color: #636467;
      }
    }
  }
}
</style>
